import React, { useEffect, useState } from 'react';
import { App as AntdApp } from 'antd';

import ReactDOM from 'react-dom/client';
import './index.css';
import reportWebVitals from './reportWebVitals';
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
} from '@apollo/client';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import App from './App';
import { setContext } from '@apollo/client/link/context';
import { getBackendUrl, getOrganization } from './utils/config';

import { RetryLink } from 'apollo-link-retry';
import AntThemeCustomizationProvider from './providers/AntThemeCustomizationProvider';

const domain = process.env.REACT_APP_AUTH0_APP_DOMAIN
  ? process.env.REACT_APP_AUTH0_APP_DOMAIN
  : 'dev-p71c71l2vmzbbzh7.us.auth0.com';
const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID
  ? process.env.REACT_APP_AUTH0_CLIENT_ID
  : 'ATeyA5oO1qdtfv2cj4oJ4nSspDzk1f5z';

const organization = getOrganization();

const backendUrl = getBackendUrl();

const audienceUrl = process.env.REACT_APP_AUTH0_AUDIENCE_URL ?? backendUrl;

const AuthApolloProvider = ({ children }: { children: JSX.Element }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [bearerToken, setBearerToken] = useState('');

  useEffect(() => {
    const getToken = async () => {
      if (isAuthenticated) {
        const token = await getAccessTokenSilently();
        setBearerToken(`Bearer ${token}`);
        window.localStorage.setItem('token', token);
      }
    };
    getToken();
  }, [getAccessTokenSilently, isAuthenticated]);

  const httpLink = createHttpLink({
    uri: backendUrl + 'graphql',
  });

  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      authorization: bearerToken,
    },
  }));

  const retryLink = new RetryLink({
    delay: {
      initial: 300,
      max: 3000,
      jitter: true,
    },
    attempts: {
      max: 5,
      retryIf: (error, _operation) => !!error,
    },
  });

  const client = new ApolloClient({
    link: authLink.concat(
      (retryLink as unknown as ApolloLink).concat(httpLink),
    ),
    cache: new InMemoryCache(),
    defaultOptions: {
      query: {
        fetchPolicy: 'network-only',
      },
      watchQuery: {
        fetchPolicy: 'network-only',
      },
      mutate: {
        fetchPolicy: 'network-only',
      },
    },
  });

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

const authParams: {
  redirect_uri: string;
  audience: string;
  organization?: string;
} = {
  redirect_uri: window.location.origin,
  audience: audienceUrl,
};

if (organization !== undefined && organization != null && organization !== '') {
  authParams.organization = organization;
}

root.render(
  <React.StrictMode>
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={authParams}
    >
      <AuthApolloProvider>
        <AntThemeCustomizationProvider>
          <AntdApp>
            <App />
          </AntdApp>
        </AntThemeCustomizationProvider>
      </AuthApolloProvider>
    </Auth0Provider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
